import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_VOICE_MS = 3 * 60 * 1000;

const pickMimeType = () => {
    if (typeof MediaRecorder === 'undefined') {
        return null;
    }

    const candidates = ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'];

    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || null;
};

export const isVoiceRecordingSupported = () =>
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined' &&
    !!pickMimeType();

export const formatRecordingTime = (elapsedMs) => {
    const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export function useVoiceRecorder({ onRecorded, onError, maxDurationMs = MAX_VOICE_MS }) {
    const [recording, setRecording] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [supported] = useState(() => isVoiceRecordingSupported());

    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const mimeTypeRef = useRef('');

    const cleanupStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const finishRecording = useCallback(() => {
        stopTimer();
        setRecording(false);
        setElapsedMs(0);
    }, [stopTimer]);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;

        if (recorder && recorder.state !== 'inactive') {
            recorder.stop();
        } else {
            finishRecording();
            cleanupStream();
        }
    }, [cleanupStream, finishRecording]);

    const startRecording = useCallback(async () => {
        if (!supported || recording) {
            return;
        }

        try {
            const mimeType = pickMimeType();

            if (!mimeType) {
                if (onError) {
                    onError('voiceUnsupported');
                }

                return;
            }

            mimeTypeRef.current = mimeType;
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            chunksRef.current = [];

            const recorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || 'audio/webm' });
                cleanupStream();
                finishRecording();

                if (blob.size > 0 && onRecorded) {
                    onRecorded(blob, mimeTypeRef.current);
                }
            };

            recorder.onerror = () => {
                cleanupStream();
                finishRecording();

                if (onError) {
                    onError('voiceRecordFailed');
                }
            };

            recorder.start(250);
            startedAtRef.current = Date.now();
            setRecording(true);
            setElapsedMs(0);
            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - startedAtRef.current;
                setElapsedMs(elapsed);

                if (elapsed >= maxDurationMs) {
                    stopRecording();
                }
            }, 200);
        } catch (err) {
            cleanupStream();
            finishRecording();

            const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');

            if (onError) {
                onError(denied ? 'voicePermissionDenied' : 'voiceRecordFailed');
            }
        }
    }, [supported, recording, onRecorded, onError, maxDurationMs, cleanupStream, finishRecording, stopRecording]);

    useEffect(
        () => () => {
            stopTimer();

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }

            cleanupStream();
        },
        [stopTimer, cleanupStream]
    );

    const toggleRecording = useCallback(() => {
        if (recording) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [recording, startRecording, stopRecording]);

    return { supported, recording, elapsedMs, startRecording, stopRecording, toggleRecording };
}
